import { makeAutoObservable, toJS } from "mobx";
import {toast} from "react-toastify";
import Analytics, { EVENTS } from "../lib/Analytics";

class EChainStore {
  _appStatusStore = null;
  _isFetching = false;
  _fetchStartTime = null;
  _fetchToken = 0;          // increments per fetch; guards against stale RESPONSES (PLEX-4228)
  _inFlightTargetID = "";   // target of the currently-running fetch (for the guard)
  _abortController = null;  // aborts the in-flight REQUEST on supersede/cancel (PLEX-4234)
  _error = null;            // last user-facing fetch error message (drives the panel error state)
  _paths = [];
  _searchSessionStore = null;
  _targetID = "";
  _userStore = null;

  constructor(userData, appStatusStore, searchSessionStore) {
    makeAutoObservable(this);
    this._userStore = userData;
    this._appStatusStore = appStatusStore;
    this._searchSessionStore = searchSessionStore;
  }

  // options.searchIDs — anchor ids of the chat's prior search, passed as an
  // EXPLICIT one-shot params override instead of mutating the shared
  // searchSessionStore.params in place (the old TargetChainInjector did the
  // latter, leaking the override into every subsequent search). PLEX-4228.
  getEChains = async (targetID, datasetsFilter = [], options = {}) => {
    const { searchIDs = null } = options;

    // In-flight guard: ignore a re-entrant call for the same target (the
    // common double-click) so we never issue overlapping fetches racing
    // their responses into .paths. A different target is allowed to
    // supersede — the token check below discards the stale response.
    if (this._isFetching && targetID === this._inFlightTargetID) return;

    // A different target supersedes the in-flight one: abort its REQUEST
    // (PLEX-4234) in addition to discarding its RESPONSE via the fetch
    // token (PLEX-4228). The guards are complementary — abort stops the
    // wire immediately; the token protects .paths if the abort races the
    // resolve. _abortInFlight() no-ops when nothing is running.
    this._abortInFlight();

    const token = ++this._fetchToken;
    this._inFlightTargetID = targetID;
    const controller = (typeof AbortController !== "undefined") ? new AbortController() : null;
    this._abortController = controller;
    this._error = null;
    this.isFetching = true;
    this._fetchStartTime = Date.now();

    // Build the one-shot params override (does NOT touch this.params). Drop
    // queryEntities: it also maps to `ids` downstream and wins the collision
    // when null, so it must not shadow the anchor searchIDs.
    let paramsOverride = null;
    if (searchIDs != null) {
      const { queryEntities: _drop, ...rest } = this._searchSessionStore.params || {};
      paramsOverride = { ...rest, searchIDs };
    }

    try {
      const progress = s => this._searchSessionStore.searchProgress = s;
      const signal = controller ? controller.signal : null;
      // Signal is the 5th positional arg; paramsOverride (null when no
      // searchIDs) sits at 4 so the abort signal can reach the fetch.
      const results = await this._searchSessionStore.fetchEvidenceChains(
        targetID, datasetsFilter, progress, paramsOverride, signal);
      if (token !== this._fetchToken) return;   // superseded by a newer fetch — drop
      this.targetID = targetID;
      this.paths = results.paths || [];
      Analytics.event(EVENTS.ECHAINS_COMPLETE, {
        target_id: targetID,
        duration_ms: Date.now() - this._fetchStartTime,
        path_count: this.paths.length,
      });
    }
    catch (error) {
      if (token !== this._fetchToken) return;   // stale failure — a newer fetch owns state
      // Cancelled/superseded request: the abort rejects the fetch, but that
      // is not a user-facing failure — swallow it (no toast, no error state).
      if (error?.name === "AbortError" || controller?.signal?.aborted) return;
      // Build the message ONCE and reuse it for both the visible toast and
      // the Error passed to react-toastify (previously double-built — 4234).
      const detail = error.message || error.detail || error.statusText || "(no details)";
      const msg = `Error on receiving echain data: ${detail}. targetId = ${targetID}`;
      this._error = msg;
      toast.error(msg, { error: new Error(msg) });
      Analytics.event(EVENTS.ECHAINS_ERROR, {
        target_id: targetID,
        duration_ms: Date.now() - this._fetchStartTime,
        error: error.message || error.detail || "(no details)",
      });
      this.paths = [];
      this.targetID = "";
    }
    finally {
      // Only the fetch that still owns the token clears the shared flags —
      // a superseded fetch must leave the newer one's state alone.
      if (token === this._fetchToken) {
        this.isFetching = false;
        this._abortController = null;
        this._inFlightTargetID = "";
      }
    }
  };

  // Abort the currently-running request, if any. Idempotent no-op otherwise.
  // Separate from cancelFetch so getEChains can supersede without emitting a
  // user-cancel analytics event.
  _abortInFlight = () => {
    if (this._abortController) {
      try { this._abortController.abort(); } catch (_) { /* ignore */ }
      this._abortController = null;
    }
  };

  cancelFetch = () => {
    if (this._isFetching && this._fetchStartTime) {
      Analytics.event(EVENTS.ECHAINS_CANCEL, {
        target_id: this._targetID,
        duration_ms: Date.now() - this._fetchStartTime,
      });
    }
    // Actually abort the in-flight request — previously this only logged
    // Analytics and let the fetch run to completion (PLEX-4234).
    this._abortInFlight();
  };

  // Reset every per-chat surface this store owns. Called from the
  // ProjectView scope effect on the shared `chatId` / `workspace:chat-scope`
  // signal (PLEX-4228) so the reset lives entirely in the store instead of
  // being split between component and store (PLEX-4234). Aborts any in-flight
  // fetch and bumps the token so a late response is discarded.
  resetScope = () => {
    this.cancelFetch();
    this._fetchToken++;        // invalidate any response still in flight
    this._inFlightTargetID = "";
    this.isFetching = false;
    this.paths = [];
    this.targetID = "";
    this._error = null;
  };

  get isFetching() {
    return this._isFetching;
  }

  set isFetching(b) {
    this._isFetching = b;
  }

  get targetID() {
    return this._targetID;
  }

  set targetID(t) {
    this._targetID = t;
  }

  get error() {
    return this._error;
  }

  set error(e) {
    this._error = e;
  }

  get searchParams() {
    return this._searchSessionStore.params;
  }

  get searchIDs() {
    return this.searchParams.searchIDs;
  }

  get directConnectionID() {
    return this.searchParams.directConnectionID;
  }

  get neighborhood() {
    return this.searchParams.neighborhood;
  }

  get simThreshold() {
    return this.searchParams.simThreshold;
  }

  get simFloor() {
    return this.searchParams.simFloor;
  }

  get dsConfig() {
    return this.searchParams.dsConfig;
  }

  get paths() {
    return toJS(this._paths);
  }

  set paths(p) {
    this._paths = p;
  }

}

export default EChainStore;
